"use client";

import * as React from "react";

import { getCtaIntent, getCtaTrackingProps } from "@/lib/cta-tracking";
import { getNodeText } from "@/lib/react-helpers";

import { useCtaLocation } from "./cta-location";
import { ExternalLink } from "./external-link";
import NextLink from "./next-link";
import type { NextLinkProps } from "./next-link";

export type LinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  Exclude<keyof NextLinkProps, "href">
> &
  NextLinkProps & {
    children?: React.ReactNode;
  } & React.RefAttributes<HTMLAnchorElement> & { external?: boolean };

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, href, external, children, ...props }, ref) => {
    const Comp = external ? ExternalLink : NextLink;
    const otherProps = external ? {} : { prefetch: false };
    const location = useCtaLocation();
    // Derived from the destination so any link to the application flow, sign
    // in or book-a-call is tagged here and pages cannot drop it. Explicit
    // props below still win.
    // Only tracked CTAs get attributes; ordinary links must keep emitting the
    // same autocapture properties as before.
    const intent = getCtaIntent(href);
    const tracking = getCtaTrackingProps({
      intent,
      location,
      label: intent ? getNodeText(children) : undefined,
    });
    return (
      <Comp
        href={href}
        className={className}
        ref={ref}
        prefetch={!external ? false : undefined}
        {...tracking}
        {...props}
        {...otherProps}
      >
        {children}
      </Comp>
    );
  }
);

Link.displayName = "Link";

export { Link };
